import { useEffect, useMemo, useState } from "react";
import api from "@/modules/growing-circle/lib/api";
import { Button } from "@/components/ui/button";
import { Globe, Eye, Info, ShieldAlert, PlayCircle, CheckCircle2 } from "lucide-react";
import AnalyticsHeatmap from "@/modules/growing-circle/pages/admin/AnalyticsHeatmap";

// Shared selected/unselected button classes used by every filter row on this
// page. Selected must be MORE visible than unselected while keeping the
// gold text readable — filled backgrounds hide the text on this dark
// theme, so we go with a translucent gold tint + solid gold border + gold
// text + subtle outer ring.
const SELECTED_BTN_CLS = "bg-primary/15 text-primary border-primary font-semibold shadow-[0_0_0_1px_hsl(var(--primary))] hover:bg-primary/20";
const UNSELECTED_BTN_CLS = "bg-transparent text-foreground border-border hover:bg-secondary/40";

// Semantic series colors kept consistent everywhere the two populations are
// compared. Gold for "all traffic", teal/sage for "consented".
const SERIES_ALL_HEX = "#c9a96e";       // gold
const SERIES_CONSENTED_HEX = "#5FB4A2"; // teal/sage — high contrast on dark

// ------- display-label normalizers (do NOT touch stored data) -------
// Consolidated referrer families used across the Sources panel. Stored
// referrer values are ``_safe_key``-escaped in Mongo, so dots become
// underscores. Match against both dotted and underscored variants.
const REFERRER_GROUPS = [
  { label: "Google",       match: /(^|[._-])google[._-]|com_google_android|googlequicksearchbox/i },
  { label: "Facebook",     match: /(^|[._-])(facebook|fb)[._-]|com_facebook_katana|l_facebook_com/i },
  { label: "Instagram",    match: /(^|[._-])instagram[._-]|com_instagram_android/i },
  { label: "LinkedIn",     match: /(^|[._-])linkedin[._-]|com_linkedin/i },
  { label: "YouTube",      match: /(^|[._-])youtube[._-]/i },
  { label: "Twitter · X",  match: /(^|[._-])(twitter|t_co|x_com)([._-]|$)/i },
  { label: "TikTok",       match: /(^|[._-])tiktok[._-]|com_zhiliaoapp_musically/i },
  { label: "Bing",         match: /(^|[._-])bing[._-]/i },
  { label: "DuckDuckGo",   match: /(^|[._-])duckduckgo[._-]/i },
  { label: "Yahoo",        match: /(^|[._-])(search_yahoo|yahoo)[._-]/i },
  { label: "Reddit",       match: /(^|[._-])reddit[._-]/i },
  { label: "Whatsapp",     match: /(^|[._-])(whatsapp|wa_me)([._-]|$)/i },
  { label: "Telegram",     match: /(^|[._-])(telegram|t_me)([._-]|$)/i },
  { label: "Newsletter (Brevo)", match: /(^|[._-])(email_brevo|brevo_email|sendinblue)([._-]|$)/i },
];
// Referrers we NEVER show as genuine acquisition traffic in production.
const INTERNAL_REFERRERS = /(^|[._-])(emergent|app_emergent_sh|preview_emergentagent|selfdoubling_preview)/i;

const normalizeSource = (raw) => {
  const s = (raw ?? "").toString().trim();
  const key = s.toLowerCase();
  if (!s || key === "-" || key === "—" || key === "$direct" || key === "direct") {
    return "Direct / sorgente non rilevata";
  }
  if (key === "internal") return "Navigazione interna al sito";
  if (INTERNAL_REFERRERS.test(key)) return "Anteprima interna Emergent";
  for (const g of REFERRER_GROUPS) if (g.match.test(key)) return g.label;
  // Fallback: strip common tokens for a cleaner label but keep the domain.
  const cleaned = s
    .replace(/^www[._]/, "")
    .replace(/_/g, ".");
  return cleaned;
};
const normalizePath = (raw) => {
  const p = (raw || "").trim();
  if (!p || p === "-" || p === "—") return "Pagina non rilevata";
  if (p === "/") return "Home";
  return p;
};
const normalizeUTMValue = (raw) => {
  const s = (raw || "").trim();
  if (!s || s === "-" || s === "—") return "Nessun UTM";
  return raw;
};

const PRESETS = [
  { key: "today",     label: "Oggi" },
  { key: "7d",        label: "Ultimi 7 giorni" },
  { key: "30d",       label: "Ultimi 30 giorni" },
  { key: "year",      label: "Anno corrente" },
  { key: "12m",       label: "Ultimi 12 mesi" },
  { key: "alltime",   label: "Storico completo" },
];

const toISO = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setUTCDate(d.getUTCDate() - n); return d; };
const monthsAgo = (n) => { const d = new Date(); d.setUTCMonth(d.getUTCMonth() - n); return d; };
const firstOfYear  = () => { const d = new Date(); return new Date(Date.UTC(d.getUTCFullYear(), 0, 1)); };

function resolveRange(preset, from, to) {
  const today = new Date();
  switch (preset) {
    case "today":     return { from: toISO(today),         to: toISO(today) };
    case "7d":        return { from: toISO(daysAgo(6)),    to: toISO(today) };
    case "30d":       return { from: toISO(daysAgo(29)),   to: toISO(today) };
    case "year":      return { from: toISO(firstOfYear()), to: toISO(today) };
    case "12m":       return { from: toISO(monthsAgo(12)), to: toISO(today) };
    case "alltime":   return { from: "alltime",            to: toISO(today) };
    default:          return { from: from || toISO(daysAgo(29)), to: to || toISO(today) };
  }
}

function fmt(n) {
  if (n == null) return "—";
  if (typeof n === "string") return n;              // already display-ready
  if (typeof n === "number" && !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("it-IT").format(n);
}

function Card({ label, value, hint, tone = "default", testid }) {
  const toneClasses =
    tone === "accent" ? "border-accent/40 bg-accent/[0.03]" :
    tone === "primary" ? "border-primary/40 bg-primary/[0.03]" :
    "border-border";
  return (
    <div className={`rounded-2xl p-5 border bg-card ${toneClasses}`} data-testid={testid}>
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">{label}</p>
      <p className="font-heading text-3xl">{fmt(value)}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{hint}</p>}
    </div>
  );
}

function GroupHeader({ tone, icon: Icon, title, subtitle, testid }) {
  const badgeCls = tone === "accent"
    ? "text-accent bg-accent/10"
    : "text-primary bg-primary/10";
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4" data-testid={testid}>
      <span className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-widest ${badgeCls} px-3 py-1 rounded-full`}>
        <Icon className="w-3.5 h-3.5" /> {title}
      </span>
      {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
    </div>
  );
}

function BarRow({ label, value, max }) {
  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="w-40 truncate text-muted-foreground" title={label}>{label}</span>
      <div className="flex-1 h-2 bg-secondary/50 rounded-full overflow-hidden">
        <div className="h-full bg-primary/60" style={{ width: `${(value / Math.max(1, max)) * 100}%` }} />
      </div>
      <span className="w-14 text-right tabular-nums">{fmt(value)}</span>
    </div>
  );
}

function List({ title, rows, empty, keyField, valueField, kind }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl p-5">
        <h4 className="text-sm font-medium mb-2">{title}</h4>
        <p className="text-xs text-muted-foreground">{empty || "Nessun dato per questo periodo."}</p>
      </div>
    );
  }
  const items = rows.map((r) => {
    let label = r[keyField] ?? r.key ?? r.country ?? r.path ?? r.referrer ?? r.utm_source ?? "—";
    if (kind === "referrer") label = normalizeSource(label);
    else if (kind === "path") label = normalizePath(label);
    else if (kind === "utm") label = normalizeUTMValue(label);
    return { label, value: Number(r[valueField] ?? r.count ?? r.visitors ?? r.pageviews ?? 0) };
  });
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <h4 className="text-sm font-medium mb-3">{title}</h4>
      <div className="space-y-1.5">
        {items.slice(0, 10).map((it, i) => <BarRow key={i} {...it} max={max} />)}
      </div>
    </div>
  );
}

function TwoColumn({ title, leftTitle, rightTitle, left, right, leftKey, leftVal, rightKey, rightVal, consentedAvailable, leftKind, rightKind }) {
  return (
    <section className="mb-10">
      <h3 className="font-heading text-xl mb-3">{title}</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <List title={leftTitle} rows={left} keyField={leftKey} valueField={leftVal} kind={leftKind} />
        {consentedAvailable
          ? <List title={rightTitle} rows={right} keyField={rightKey} valueField={rightVal} kind={rightKind} />
          : <div className="bg-card border border-dashed border-border rounded-2xl p-5 flex items-center gap-2 text-xs text-muted-foreground">
              <Info className="w-4 h-4" />
              I dati dettagliati con consenso non sono ancora disponibili in questo ambiente.
            </div>}
      </div>
    </section>
  );
}

function DailyBars({ days, consentedAvailable }) {
  if (!days || days.length === 0) return null;
  const max = Math.max(1, ...days.map((d) => Math.max(d.estimated_visitors || 0, d.consented_visitors || 0)));
  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading text-xl">Visitatori per giorno</h3>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: SERIES_ALL_HEX }}></span> Tutti i visitatori</span>
          {consentedAvailable && <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: SERIES_CONSENTED_HEX }}></span> Con consenso</span>}
        </div>
      </div>
      <div className="flex items-end gap-1 h-44 overflow-x-auto pb-2" data-testid="daily-chart">
        {days.map((d) => (
          <div key={d.date} className="flex flex-col items-center min-w-[26px]"
               title={`${d.date}: tutti ${d.estimated_visitors} · con consenso ${d.consented_visitors ?? "—"} · reg. ${d.registrations}`}>
            <div className="flex items-end gap-0.5 h-32 w-full">
              <div className="w-2.5 rounded-t-sm transition-all" style={{ height: `${((d.estimated_visitors || 0) / max) * 100}%`, background: SERIES_ALL_HEX }} />
              {consentedAvailable && <div className="w-2.5 rounded-t-sm transition-all" style={{ height: `${((d.consented_visitors || 0) / max) * 100}%`, background: SERIES_CONSENTED_HEX }} />}
            </div>
            <span className="text-[9px] mt-2 text-muted-foreground whitespace-nowrap rotate-45 origin-left">{d.date.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Funnel({ steps }) {
  if (!steps) return null;
  const max = Math.max(1, ...steps.map((s) => s.count || 0));
  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10" data-testid="conversion-funnel">
      <h3 className="font-heading text-xl mb-4">Registrazioni e conversioni</h3>
      <div className="space-y-2">
        {steps.map((s) => (
          <div key={s.key} className="flex items-center gap-3" data-testid={`funnel-${s.key}`}>
            <span className="w-52 text-sm text-muted-foreground truncate">{s.label}</span>
            <div className="flex-1 h-3 bg-secondary/50 rounded-full overflow-hidden">
              <div className="h-full bg-accent/70" style={{ width: `${((s.count || 0) / max) * 100}%` }} />
            </div>
            <span className="w-16 text-right font-heading text-lg">{fmt(s.count)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IdentifiedTable({ rows }) {
  if (!rows || rows.length === 0) return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <p className="text-xs text-muted-foreground">Nessuna persona registrata nel periodo selezionato.</p>
    </div>
  );
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs" data-testid="identified-table">
          <thead className="text-muted-foreground text-left bg-secondary/30">
            <tr>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Paese</th>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Provenienza</th>
              <th className="px-4 py-3">UTM</th>
              <th className="px-4 py-3">Browser · Dispositivo</th>
              <th className="px-4 py-3">Stato</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`identified-${r.id}`}>
                <td className="px-4 py-2.5">{r.first_name} {r.surname}</td>
                <td className="px-4 py-2.5 break-all">{r.email}</td>
                <td className="px-4 py-2.5">{r.country || "—"}</td>
                <td className="px-4 py-2.5 font-mono text-[11px]">{r.ip_address || "—"}</td>
                <td className="px-4 py-2.5 tabular-nums">{(r.timestamp || "").slice(0, 10)}</td>
                <td className="px-4 py-2.5 truncate max-w-[160px]">{normalizeSource(r.referrer) || "Direct / sorgente non rilevata"}</td>
                <td className="px-4 py-2.5 truncate max-w-[160px]">
                  {(() => {
                    const parts = r.utm ? Object.values(r.utm).filter(Boolean) : [];
                    return parts.length ? parts.join(" · ") : "Nessun UTM";
                  })()}
                </td>
                <td className="px-4 py-2.5 truncate max-w-[160px]">
                  {[r.browser, r.device, r.os].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="px-4 py-2.5">
                  {r.gc_status === "active"
                    ? <span className="text-accent">attivo</span>
                    : r.verification_status || (r.handoff_status ?? "—")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [topTab, setTopTab] = useState("overview");
  const [preset, setPreset] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [overview, setOverview] = useState(null);
  const [daily, setDaily] = useState(null);
  const [geo, setGeo] = useState(null);
  const [sources, setSources] = useState(null);
  const [pages, setPages] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [identified, setIdentified] = useState(null);
  const [loading, setLoading] = useState(true);
  const [baseline, setBaseline] = useState(null);
  const [baselineLoaded, setBaselineLoaded] = useState(false);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    api.get("/admin/analytics/settings/baseline")
      .then((r) => setBaseline(r.data?.started_at || null))
      .catch(() => {})
      .finally(() => setBaselineLoaded(true));
  }, []);

  async function startBaseline() {
    if (starting) return;
    if (!window.confirm(
      "Confermi l'avvio dell'analitica di produzione?\n\n" +
      "Da questo momento in poi il Back Office mostrerà SOLO i dati generati dopo l'attivazione. " +
      "Non è possibile annullare o modificare questa impostazione. " +
      "Nessun dato storico verrà eliminato."
    )) return;
    setStarting(true);
    try {
      const r = await api.post("/admin/analytics/settings/baseline");
      setBaseline(r.data?.started_at || null);
    } finally { setStarting(false); }
  }

  const range = useMemo(
    () => resolveRange(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const params = { from: range.from, to: range.to };
    Promise.all([
      api.get("/admin/analytics/overview", { params }),
      api.get("/admin/analytics/daily", { params }),
      api.get("/admin/analytics/geography", { params }),
      api.get("/admin/analytics/sources", { params }),
      api.get("/admin/analytics/pages", { params }),
      api.get("/admin/analytics/funnel", { params }),
      api.get("/admin/analytics/visitors/identified", { params }),
    ]).then((res) => {
      if (cancel) return;
      setOverview(res[0].data);
      setDaily(res[1].data);
      setGeo(res[2].data);
      setSources(res[3].data);
      setPages(res[4].data);
      setFunnel(res[5].data);
      setIdentified(res[6].data);
    }).catch(() => {}).finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range.from, range.to]);

  const consentedAvailable = overview?.consented?.available;

  return (
    <div data-testid="admin-analytics-page" className="pb-20">
      {/* Header + top-level tabs */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Analytics</h1>
          <p className="text-muted-foreground text-sm mt-1">Panoramica del traffico e delle conversioni di selfdoubling.com.</p>
        </div>
        <div className="text-xs text-right" data-testid="baseline-widget">
          {baselineLoaded && baseline ? (
            <span className="inline-flex items-center gap-2 text-accent bg-accent/10 border border-accent/30 rounded-full px-3 py-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Analitica di produzione attiva dal {new Date(baseline).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short", timeZone: "UTC" })} UTC
            </span>
          ) : baselineLoaded ? (
            <Button size="sm" onClick={startBaseline} disabled={starting}
                    className={SELECTED_BTN_CLS + " rounded-full"}
                    data-testid="start-production-baseline">
              <PlayCircle className="w-4 h-4 mr-1.5" />
              {starting ? "Attivazione…" : "Avvia analitica di produzione"}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="flex gap-2 border-b border-border mb-8">
        <button className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
          topTab === "overview" ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
        }`} onClick={() => setTopTab("overview")} data-testid="tab-overview">Panoramica</button>
        <button className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
          topTab === "heatmap" ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
        }`} onClick={() => setTopTab("heatmap")} data-testid="tab-heatmap">Heatmap</button>
      </div>

      {topTab === "heatmap" ? <AnalyticsHeatmap /> : (
      <>
      {/* Presets */}
      <div className="flex flex-wrap items-center gap-2 mb-8">
          {PRESETS.map((p) => (
            <Button key={p.key} size="sm"
                    variant="outline"
                    className={`rounded-full text-xs uppercase tracking-[0.14em] h-8 border ${preset === p.key ? SELECTED_BTN_CLS : UNSELECTED_BTN_CLS}`}
                    onClick={() => setPreset(p.key)}
                    data-testid={`preset-${p.key}`}>
              {p.label}
            </Button>
          ))}
          <div className="flex items-center gap-1 ml-2 text-xs">
            <input type="date"
                   className="bg-background border border-border rounded-lg px-2 py-1"
                   value={preset === "custom" ? customFrom : range.from}
                   onChange={(e) => { setPreset("custom"); setCustomFrom(e.target.value); if (!customTo) setCustomTo(range.to); }}
                   data-testid="from-input" />
            <span className="text-muted-foreground">→</span>
            <input type="date"
                   className="bg-background border border-border rounded-lg px-2 py-1"
                   value={preset === "custom" ? customTo : range.to}
                   onChange={(e) => { setPreset("custom"); setCustomTo(e.target.value); if (!customFrom) setCustomFrom(range.from); }}
                   data-testid="to-input" />
          </div>
      </div>

      {loading && <p className="text-muted-foreground text-sm mb-6">Caricamento…</p>}

      {/* Group A — All traffic */}
      <section className="mb-10">
        <GroupHeader tone="primary" icon={Globe}
                     title="Tutto il traffico del sito"
                     subtitle="Include anche i visitatori senza consenso analytics."
                     testid="group-all-traffic" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="Visualizzazioni" value={overview?.all_traffic?.pageviews} tone="primary" testid="card-all-pageviews" />
          <Card label="Visitatori stimati" value={overview?.all_traffic?.estimated_unique_visitors} hint="Stima aggregata, ricalcolata ogni giorno." tone="primary" testid="card-all-visitors" />
          <Card label="Registrazioni" value={overview?.all_traffic?.registrations} testid="card-all-registrations" />
          <Card label="Copertura con consenso"
                value={(() => {
                  const c = overview?.coverage_percent;
                  if (c === 0) return "0%";
                  if (typeof c === "number" && Number.isFinite(c)) return `${c}%`;
                  return null; // Card renders "—" for null
                })()}
                hint="Percentuale di visitatori unici osservabili in dettaglio."
                testid="card-coverage" />
        </div>
      </section>

      {/* Group B — Consented */}
      <section className="mb-10">
        <GroupHeader tone="accent" icon={Eye}
                     title="Traffico con consenso analytics"
                     subtitle="Dati dettagliati solo per i visitatori che hanno accettato la misurazione."
                     testid="group-consented" />
        {consentedAvailable === false && (
          <div className="bg-secondary/40 border border-border rounded-2xl p-4 flex items-start gap-2 text-xs text-muted-foreground mb-4">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Le metriche dettagliate non sono ancora attive: mancano le chiavi di configurazione analytics riservate. Chiedi all'amministratore tecnico di completare la configurazione.</span>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="Visitatori con consenso" value={overview?.consented?.visitors} tone="accent" testid="card-consented-visitors" />
          <Card label="Visite" value={overview?.consented?.sessions} tone="accent" testid="card-consented-sessions" />
          <Card label="Visualizzazioni" value={overview?.consented?.pageviews} tone="accent" testid="card-consented-pageviews" />
        </div>
      </section>

      {/* Daily chart */}
      {daily?.days && <DailyBars days={daily.days} consentedAvailable={consentedAvailable} />}

      {/* Geography */}
      <TwoColumn title="Paesi"
                 leftTitle="Tutto il traffico"
                 rightTitle="Con consenso"
                 left={geo?.all_traffic}
                 right={geo?.consented}
                 leftKey="country" leftVal="count"
                 rightKey="country" rightVal="visitors"
                 consentedAvailable={consentedAvailable} />

      {/* Sources */}
      <section className="mb-10">
        <h3 className="font-heading text-xl mb-3">Da dove arrivano i visitatori</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <List title="Provenienze — tutto il traffico" rows={sources?.all_traffic?.referrers} keyField="referrer" valueField="count" kind="referrer" />
            {sources?.all_traffic?.refs && sources.all_traffic.refs.length > 0 && (
              <div className="bg-card border border-border rounded-2xl p-5" data-testid="ref-codes-list">
                <h4 className="text-sm font-medium mb-3">Provenienza attribuita <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-1">privata</span></h4>
                <div className="space-y-1.5">
                  {sources.all_traffic.refs.map((r, i) => (
                    <BarRow key={i} label={r.label || r.code} value={r.count} max={Math.max(1, ...sources.all_traffic.refs.map((x) => x.count))} />
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <List title="UTM source" rows={sources?.all_traffic?.utm_source} keyField="utm_source" valueField="count" kind="utm" />
              <List title="UTM medium" rows={sources?.all_traffic?.utm_medium} keyField="utm_medium" valueField="count" kind="utm" />
              <List title="UTM campaign" rows={sources?.all_traffic?.utm_campaign} keyField="utm_campaign" valueField="count" kind="utm" />
            </div>
          </div>
          {consentedAvailable
            ? <List title="Provenienze — con consenso" rows={sources?.consented} keyField="referrer" valueField="visitors" kind="referrer" />
            : <div className="bg-card border border-dashed border-border rounded-2xl p-5 flex items-center gap-2 text-xs text-muted-foreground">
                <Info className="w-4 h-4" /> I dati dettagliati con consenso non sono ancora disponibili in questo ambiente.
              </div>}
        </div>
      </section>

      {/* Pages */}
      <TwoColumn title="Pagine più viste"
                 leftTitle="Tutto il traffico"
                 rightTitle="Con consenso"
                 left={pages?.all_traffic}
                 right={pages?.consented}
                 leftKey="path" leftVal="count"
                 rightKey="path" rightVal="pageviews"
                 leftKind="path" rightKind="path"
                 consentedAvailable={consentedAvailable} />

      {/* Funnel */}
      <Funnel steps={funnel?.steps} />

      {/* Identified */}
      <section className="mb-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h3 className="font-heading text-xl">Persone identificate</h3>
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-destructive/80 bg-destructive/10 px-2 py-0.5 rounded-full">
            <ShieldAlert className="w-3 h-3" /> Dati personali identificativi
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Dati personali associati a utenti identificati, accessibili solo agli amministratori autorizzati del Back Office.
        </p>
        <IdentifiedTable rows={identified?.visitors} />
      </section>

      <p className="text-[10px] text-muted-foreground text-center leading-relaxed mt-10 max-w-2xl mx-auto">
        L'indirizzo IP dei visitatori anonimi non viene mai memorizzato. Le statistiche "Tutto il traffico" usano un identificatore anonimo che si rigenera ogni giorno e non consente di seguire una persona da un giorno all'altro. Gli indirizzi IP delle persone registrate sono conservati solo per la verifica dell'account e sono visibili unicamente qui in questa pagina.
      </p>
      </>
      )}
    </div>
  );
}
